import axios from 'axios'
import { ElMessage } from 'element-plus'

const http = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 10000,
})

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token')
  if (token) config.headers.Authorization = 'Bearer ' + token
  return config
})

function redirectToLogin() {
  localStorage.removeItem('admin_token')
  localStorage.removeItem('admin_username')
  if (!location.hash.startsWith('#/login')) {
    window.location.href = '/#/login'
  }
}

http.interceptors.response.use(
  (res) => {
    const data = res.data
    if (data.code !== 0) {
      // token 过期/无效：后端以 HTTP 200 + code:401 返回，需在此拦截并跳转登录；
      // 但登录接口本身的 401（用户名或密码错误）要正常提示，不当作过期
      const isLoginReq = (res.config?.url || '').includes('/admin/login')
      if (data.code === 401 && !isLoginReq) {
        ElMessage.error('登录已过期，请重新登录')
        redirectToLogin()
        return Promise.reject(data)
      }
      ElMessage.error(data.message || '请求失败')
      return Promise.reject(data)
    }
    return data.data
  },
  (err) => {
    if (err.response?.status === 401) {
      redirectToLogin()
    }
    ElMessage.error(err.message || '网络错误')
    return Promise.reject(err)
  }
)

// 文件下载（Excel 等二进制）：单独走 blob，不经过上面的 JSON 拦截器
const fileHttp = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 60000,
  responseType: 'blob',
})
fileHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token')
  if (token) config.headers.Authorization = 'Bearer ' + token
  return config
})

// downloadFile 发起下载并保存为文件；后端出错（返回 JSON）时提示错误信息
async function downloadFile(url, params, fallbackName) {
  let res
  try {
    res = await fileHttp.get(url, { params })
  } catch (err) {
    ElMessage.error(err.message || '下载失败')
    throw err
  }
  const blob = res.data
  // 后端以 JSON 返回即出错（如权限/参数问题）
  if (blob.type && blob.type.includes('application/json')) {
    let msg = '导出失败'
    try { msg = JSON.parse(await blob.text()).message || msg } catch { /* ignore */ }
    ElMessage.error(msg)
    throw new Error(msg)
  }
  let name = fallbackName
  const cd = res.headers['content-disposition'] || ''
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd)
  if (m) { try { name = decodeURIComponent(m[1]) } catch { /* keep fallback */ } }
  const objUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objUrl
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(objUrl)
}

// 管理员认证
export const adminLogin = (data) => http.post('/admin/login', data)
export const getMe = () => http.get('/admin/me')
export const changeMyPassword = (data) => http.post('/admin/change-password', data)

// 后台用户管理（仅超管）
export const getPermissionCatalog = () => http.get('/admin/permissions/catalog')
export const getAdminUsers = () => http.get('/admin/admin-users')
export const createAdminUser = (data) => http.post('/admin/admin-users', data)
export const updateAdminUser = (id, data) => http.put(`/admin/admin-users/${id}`, data)
export const resetAdminUserPassword = (id, password) => http.put(`/admin/admin-users/${id}/password`, { password })
export const deleteAdminUser = (id) => http.delete(`/admin/admin-users/${id}`)

// 教师
export const getTeachers = (params) => http.get('/admin/teachers', { params })
export const createTeacher = (data) => http.post('/admin/teachers', data)
export const updateTeacher = (id, data) => http.put(`/admin/teachers/${id}`, data)
export const toggleTeacherRecommend = (id) => http.put(`/admin/teachers/${id}/recommend`)
export const uploadTeacherAvatar = (id, file) => {
  const form = new FormData()
  form.append('avatar', file)
  return http.post(`/admin/teachers/${id}/avatar`, form)
}
export const uploadTeacherPhoto = (id, file) => {
  const form = new FormData()
  form.append('photo', file)
  return http.post(`/admin/teachers/${id}/photos`, form)
}
export const deleteTeacherPhoto = (id, url) => http.delete(`/admin/teachers/${id}/photos`, { data: { url } })
export const resetTeacherPassword = (id, password) => http.put(`/admin/teachers/${id}/password`, { password })

// 计费档位
export const getTiers = () => http.get('/admin/tiers')
export const createTier = (data) => http.post('/admin/tiers', data)
export const updateTier = (id, data) => http.put(`/admin/tiers/${id}`, data)
export const deleteTier = (id) => http.delete(`/admin/tiers/${id}`)

// 收银台
export const getConsoleOverview = () => http.get('/admin/console/overview')
// 后台开台（收费房记线下台费）
export const openRoom = (id, data) => http.post(`/admin/console/rooms/${id}/open`, data)
export const printRoomConsumption = (id) => http.post(`/admin/console/rooms/${id}/print-consumption`)
export const transferRoomConsumption = (id, targetRoomId) => http.post(`/admin/console/rooms/${id}/transfer`, { target_room_id: targetRoomId })

// 营销·公众号粉丝
export const syncOAFollowers = () => http.post('/admin/oa/sync')
export const getOAFollowers = (params) => http.get('/admin/oa/followers', { params })
export const getOAOverview = () => http.get('/admin/oa/overview')
export const getOAFollowerDetail = (id) => http.get(`/admin/oa/followers/${id}`)

// 营销·小程序会员
export const recomputeMembers = () => http.post('/admin/members/recompute')
export const getMemberOverview = () => http.get('/admin/members/overview')
export const getMembers = (params) => http.get('/admin/members', { params })
export const getMemberDetail = (id) => http.get(`/admin/members/${id}`)
export const getRecallPreview = () => http.get('/admin/members/recall/preview')
export const recallMembers = () => http.post('/admin/members/recall')
export const recallMember = (id) => http.post(`/admin/members/${id}/recall`)
// 收银台·老师工作台
export const getConsoleTeacher = (id) => http.get(`/admin/console/teachers/${id}`)
export const teacherAttendanceToggle = (id) => http.post(`/admin/console/teachers/${id}/attendance-toggle`)
export const consoleHoldLesson = (id) => http.post(`/admin/console/lessons/${id}/hold`)
export const heldCollectOffline = (id, data) => http.post(`/admin/console/held/${id}/collect-offline`, data)
// 流程②：挂单结账各计费档位金额（服务端算价）
export const getHeldCollectOptions = (id) => http.get(`/admin/console/held/${id}/collect-options`)

// 呼叫服务员
export const getServiceCalls = () => http.get('/admin/service-calls')
export const handleServiceCall = (id) => http.put(`/admin/service-calls/${id}/handle`)

// 上课订单
export const getLessons = (params) => http.get('/admin/lessons', { params })
export const exportLessons = (params) => downloadFile('/admin/lessons/export', params, '上课订单.xlsx')
export const updateLessonPayMethod = (id, payload) => http.put(`/admin/lessons/${id}/pay-method`, payload)
export const voidLesson = (id, reason) => http.post(`/admin/lessons/${id}/void`, { reason })

// 开发调试：分账流程测试
export const devListLessons = (params) => http.get('/admin/dev/lessons', { params })
export const devMockPay = (lessonId) => http.post('/admin/dev/mock-pay', { lesson_id: lessonId })
export const devExpedite = (id) => http.post(`/admin/dev/lessons/${id}/expedite`)
export const devSetAccount = (id, accountId) => http.post(`/admin/dev/lessons/${id}/set-account`, { account_id: accountId })
export const devResetSettle = (id) => http.post(`/admin/dev/lessons/${id}/reset-settle`)
export const devRunSettlement = () => http.post('/admin/dev/run-settlement')

// 老师分组
export const getGroups = () => http.get('/admin/groups')
export const createGroup = (data) => http.post('/admin/groups', data)
export const updateGroup = (id, data) => http.put(`/admin/groups/${id}`, data)
export const deleteGroup = (id) => http.delete(`/admin/groups/${id}`)

// 轮播图
export const getBanners = () => http.get('/admin/banners')
export const uploadBanner = (file) => {
  const form = new FormData()
  form.append('image', file)
  return http.post('/admin/banners', form)
}
export const updateBanner = (id, data) => http.put(`/admin/banners/${id}`, data)
export const deleteBanner = (id) => http.delete(`/admin/banners/${id}`)

// 门票
export const getTickets = () => http.get('/admin/tickets')
export const createTicket = (data) => http.post('/admin/tickets', data)
export const updateTicket = (id, data) => http.put(`/admin/tickets/${id}`, data)
export const getTicketOrders = () => http.get('/admin/ticket-orders')

// 礼物
export const getGifts = () => http.get('/admin/gifts')
export const createGift = (data) => http.post('/admin/gifts', data)
export const updateGift = (id, data) => http.put(`/admin/gifts/${id}`, data)
export const getGiftOrders = () => http.get('/admin/gift-orders')
export const getGiftRecords = (params) => http.get('/admin/gift-records', { params })

// 考勤
export const getAttendance = (params) => http.get('/admin/attendance', { params })

// 收益
export const getEarnings = (params) => http.get('/admin/earnings', { params })

// 报表
export const getReportFlow = (params) => http.get('/admin/report/flow', { params })
export const getReportLessonDaily = (params) => http.get('/admin/report/lesson-daily', { params })
export const getReportTeacherGifts = (params) => http.get('/admin/report/teacher-gifts', { params })
export const getReportTeacherAttendance = (params) => http.get('/admin/report/teacher-attendance', { params })
export const getReportAnalytics = (params) => http.get('/admin/report/analytics', { params })
export const getReportStudents  = (params) => http.get('/admin/report/students',  { params })
export const getReportLessonIncome = (params) => http.get('/admin/report/lesson-income', { params })
export const getReportLessonIncomeDetail = (params) => http.get('/admin/report/lesson-income/detail', { params })
export const getReportPendingSettlement = () => http.get('/admin/report/pending-settlement')
export const getOperationLogs = (params) => http.get('/admin/operation-logs', { params })

// 参数配置
export const getSettings = () => http.get('/admin/settings')
export const getBusinessDayStart = () => http.get('/admin/business-day-start')
export const updateSetting = (key, data) => http.put(`/admin/settings/${key}`, data)
export const uploadLogo = (file) => {
  const form = new FormData()
  form.append('file', file)
  return http.post('/admin/logo', form)
}
export const uploadImage = (file) => {
  const form = new FormData()
  form.append('file', file)
  return http.post('/admin/upload-image', form)
}

// 包厢
export const getRooms = () => http.get('/admin/rooms')
export const createRoom = (data) => http.post('/admin/rooms', data)
export const updateRoom = (id, data) => http.put(`/admin/rooms/${id}`, data)
export const getRoomOrders = (params) => http.get('/admin/room-orders', { params })
export const getRoomDetail = (id) => http.get(`/admin/rooms/${id}/detail`)
export const closeRoom = (id) => http.put(`/admin/rooms/${id}/close`)
export const restoreRoom = (id) => http.put(`/admin/rooms/${id}/restore`)
export const getRoomGiftOrders = (params) => http.get('/admin/room-gift-orders', { params })
export const deliverRoomGiftOrder = (id) => http.put(`/admin/room-gift-orders/${id}/deliver`)

// 商品
export const getAdminProducts = () => http.get('/admin/products')
export const createProduct = (data) => http.post('/admin/products', data)
export const updateProduct = (id, data) => http.put(`/admin/products/${id}`, data)
export const deleteProduct = (id) => http.delete(`/admin/products/${id}`)
// 存货（两层）：CRUD + 单位 + 入库(加权平均) + 盘点 + 流水
export const getInventoryItems = (params) => http.get('/admin/inventory/items', { params })
export const createInventoryItem = (data) => http.post('/admin/inventory/items', data)
export const updateInventoryItem = (id, data) => http.put(`/admin/inventory/items/${id}`, data)
export const deleteInventoryItem = (id) => http.delete(`/admin/inventory/items/${id}`)
export const addInventoryUnit = (id, data) => http.post(`/admin/inventory/items/${id}/units`, data)
export const deleteInventoryUnit = (uid) => http.delete(`/admin/inventory/units/${uid}`)
// 单位字典
export const getUnits = () => http.get('/admin/units')
export const createUnit = (data) => http.post('/admin/units', data)
export const updateUnit = (id, data) => http.put(`/admin/units/${id}`, data)
export const deleteUnit = (id) => http.delete(`/admin/units/${id}`)
export const stockInbound = (data) => http.post('/admin/inventory/inbound', data)
export const stockTake = (data) => http.post('/admin/inventory/stocktake', data)
export const getStockMovements = (params) => http.get('/admin/stock/movements', { params })
export const getInbounds = (params) => http.get('/admin/inventory/inbounds', { params })
export const getInboundDetail = (batch) => http.get(`/admin/inventory/inbounds/${batch}`)
export const revokeInbound = (batch) => http.post(`/admin/inventory/inbounds/${batch}/revoke`)
export const getProductOrders = (params) => http.get('/admin/product-orders', { params })
export const exportProductOrders = (params) => downloadFile('/admin/product-orders/export', params, '商品订单.xlsx')
export const confirmDelivery = (id) => http.put(`/admin/product-orders/${id}/deliver`)
export const updateProductOrderPayMethod = (id, payload) => http.put(`/admin/product-orders/${id}/pay-method`, payload)
export const voidProductOrder = (id, reason) => http.post(`/admin/product-orders/${id}/void`, { reason })
// 核券兑换（美团/抖音）
export const getVoucherProducts = (channel) => http.get('/admin/voucher-products', { params: { channel } })
export const redeemVoucher = (data) => http.post('/admin/voucher-redeem', data)
export const cancelVoucher = (orderId) => http.post('/admin/voucher-cancel', { order_id: orderId })

// 收银台线下下单
export const getOrderProducts = () => http.get('/admin/order-products')
export const getOfflinePayMethods = () => http.get('/admin/offline-pay-methods')
export const getAllOfflinePayMethods = () => http.get('/admin/offline-pay-methods/all')
export const createOfflineOrder = (data) => http.post('/admin/product-order-offline', data)
export const lessonOfflineCollect = (id, data) => http.post(`/admin/lessons/${id}/offline-collect`, data)
export const holdOfflineOrder = (data) => http.post('/admin/product-order-hold', data)
export const collectHeldProductOrders = (ids, paymentSplits) => http.post('/admin/product-order-hold/collect', { ids, payment_splits: paymentSplits })
export const printHeldProductOrders = (roomId) => http.post('/admin/product-order-hold/print', { room_id: roomId })
export const voidHeldProductOrder = (id) => http.post(`/admin/product-order-hold/${id}/void`)

// 商品毛利报表
export const getReportProductMargin = (params) => http.get('/admin/report/product-margin', { params })

// 云打印机
export const getPrinters = () => http.get('/admin/printers')
export const addPrinter = (data) => http.post('/admin/printers', data)
export const updatePrinter = (id, data) => http.put(`/admin/printers/${id}`, data)
export const deletePrinter = (id) => http.delete(`/admin/printers/${id}`)
export const testPrinter = (id) => http.post(`/admin/printers/${id}/test`)
export const getReceiptTypes = () => http.get('/admin/receipt-types')
export const updateReceiptTypes = (data) => http.put('/admin/receipt-types', data)

// 存取酒
export const getWineStorages = (params) => http.get('/admin/wine-storages', { params })
export const exportWineStorages = (params) => downloadFile('/admin/wine-storages/export', params, '存酒台账.xlsx')
export const storeWine = (data) => http.post('/admin/wine-storages', data)
export const takeWine = (id, data) => http.post(`/admin/wine-storages/${id}/take`, data)
export const getWineStorageLogs = (id) => http.get(`/admin/wine-storages/${id}/logs`)

// 分账
export const getRoyaltyBalance = (params) => http.get('/admin/royalty/balance', { params })
export const initiateRoyalty = (data) => http.post('/admin/royalty/share', data)
export const getRoyaltyTeachers = () => http.get('/admin/royalty/teachers')
export const getRoyaltyRecords = (params) => http.get('/admin/royalty/records', { params })
